import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";

import { MainLayout } from "../../layout/mainLayout";
import { MuiDataGrid } from "../../framework/muiDataGrid";
import { LinkTabs } from "../../framework/linkTabs";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    fetchDriverTachogramDownloads,
    fetchVehicleTachogramDownloads,
} from "../../../store/tachogramDownloadSlice";
import { TachogramMediaItem } from "../../../services/tachogramDownloadService";
import { MuiDateTimePicker } from "../../framework/muiDateTimePicker";
import { Base } from "../../../framework/base";

interface DriverFileRow extends TachogramMediaItem {
    employeeName: string;
    tachoCardId: string;
}

interface VehicleFileRow extends TachogramMediaItem {
    registerNumber: string;
}

interface EmployeeWithoutCard {
    employeeId: string;
    employeeName: string;
    statusMessage: string;
}

const BASE_PATH = "/fleetmanagementview/tachogramdownloads";

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatUnixTimestamp = (timestamp: number): string => {
    if (!timestamp) return "";
    // Convert unix timestamp (seconds) to milliseconds
    return Base.dayjsDateToDateTimeStr(timestamp * 1000);
};

export const TachogramDownloadsView = () => (
    <MainLayout>
        <TachogramDownloadsViewContent />
    </MainLayout>
);

const TachogramDownloadsViewContent = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        driverDownloads,
        vehicleDownloads,
        driverFilesLoading,
        vehicleFilesLoading,
    } = useAppSelector((s) => s.tachogramDownload);

    const [searchText, setSearchText] = useState("");
    const [fromDate, setFromDate] = useState<string>(
        Base.dayjsToJsonDate(Base.dateAddMonths(new Date(), -1))
    );
    const [toDate, setToDate] = useState<string>(
        Base.dayjsToJsonDate(new Date())
    );

    useEffect(() => {
        dispatch(fetchDriverTachogramDownloads() as any);
        dispatch(fetchVehicleTachogramDownloads() as any);
    }, [dispatch]);

    const flattenedDriverFiles: DriverFileRow[] = useMemo(() => {
        return driverDownloads
            .filter((d) => d.hasDriverCard)
            .flatMap((d) =>
                d.files.map((f) => ({
                    ...f,
                    employeeName: d.employeeName,
                    tachoCardId: d.tachoCardId ?? "",
                }))
            );
    }, [driverDownloads]);

    const employeesWithoutCard: EmployeeWithoutCard[] = useMemo(() => {
        return driverDownloads
            .filter((d) => !d.hasDriverCard)
            .map((d) => ({
                employeeId: d.employeeId,
                employeeName: d.employeeName,
                statusMessage: t(
                    "fleetManagementView.tachogramDownloads.noDriverCard"
                ),
            }));
    }, [driverDownloads]);

    const flattenedVehicleFiles: VehicleFileRow[] = useMemo(() => {
        return vehicleDownloads.flatMap((v) =>
            v.files.map((f) => ({
                ...f,
                registerNumber: v.registerNumber,
            }))
        );
    }, [vehicleDownloads]);

    const filterDriverFiles = useCallback(
        (files: DriverFileRow[]): DriverFileRow[] => {
            return files.filter((file) => {
                if (fromDate) {
                    // Convert unix timestamp (seconds) to milliseconds
                    const fileTimestamp = file.created * 1000;
                    const fromDateStart = Base.dateStartOf(fromDate, "day");
                    if (Base.isBefore(fileTimestamp, fromDateStart)) return false;
                }
                if (toDate) {
                    const fileTimestamp = file.created * 1000;
                    const toDateEnd = Base.dateEndOf(toDate, "day");
                    if (Base.isAfter(fileTimestamp, toDateEnd)) return false;
                }
                if (searchText) {
                    const lowerSearch = searchText.toLowerCase();
                    return [
                        file.name,
                        file.employeeName,
                        file.tachoCardId,
                        file.meta?.driver_name ?? "",
                    ].some(
                        (field) =>
                            field && field.toLowerCase().includes(lowerSearch)
                    );
                }
                return true;
            });
        },
        [fromDate, toDate, searchText]
    );

    const filterVehicleFiles = useCallback(
        (files: VehicleFileRow[]): VehicleFileRow[] => {
            return files.filter((file) => {
                if (fromDate) {
                    const fileTimestamp = file.created * 1000;
                    const fromDateStart = Base.dateStartOf(fromDate, "day");
                    if (Base.isBefore(fileTimestamp, fromDateStart)) return false;
                }
                if (toDate) {
                    const fileTimestamp = file.created * 1000;
                    const toDateEnd = Base.dateEndOf(toDate, "day");
                    if (Base.isAfter(fileTimestamp, toDateEnd)) return false;
                }
                if (searchText) {
                    const lowerSearch = searchText.toLowerCase();
                    return [
                        file.name,
                        file.registerNumber,
                        file.meta?.vehicle_registration ?? "",
                        file.meta?.vin ?? "",
                    ].some(
                        (field) =>
                            field && field.toLowerCase().includes(lowerSearch)
                    );
                }
                return true;
            });
        },
        [fromDate, toDate, searchText]
    );

    const filteredDriverFiles = useMemo(
        () => filterDriverFiles(flattenedDriverFiles),
        [flattenedDriverFiles, filterDriverFiles]
    );

    const filteredVehicleFiles = useMemo(
        () => filterVehicleFiles(flattenedVehicleFiles),
        [flattenedVehicleFiles, filterVehicleFiles]
    );

    const driverFileColumns: GridColDef<DriverFileRow>[] = useMemo(
        () => [
            {
                field: "created",
                headerName: t("fleetManagementView.tachogramDownloads.created"),
                flex: 1.5,
                valueFormatter: (params) =>
                    formatUnixTimestamp(params.value as number),
            },
            {
                field: "name",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.fileName"
                ),
                flex: 2,
            },
            {
                field: "employeeName",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.employeeName"
                ),
                flex: 1.5,
            },
            {
                field: "tachoCardId",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.cardNumber"
                ),
                flex: 1.5,
            },
            {
                field: "size",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.fileSize"
                ),
                flex: 1,
                valueFormatter: (params) =>
                    formatFileSize(params.value as number),
            },
            {
                field: "download",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.download"
                ),
                flex: 0.5,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <IconButton
                        size="small"
                        onClick={() => window.open(params.row.url, "_blank")}
                    >
                        <DownloadIcon />
                    </IconButton>
                ),
            },
        ],
        [t]
    );

    const noCardColumns: GridColDef<EmployeeWithoutCard>[] = useMemo(
        () => [
            {
                field: "employeeName",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.employeeName"
                ),
                flex: 2,
            },
            {
                field: "statusMessage",
                headerName: t("fleetManagementView.tachogramDownloads.status"),
                flex: 2,
            },
        ],
        [t]
    );

    const vehicleFileColumns: GridColDef<VehicleFileRow>[] = useMemo(
        () => [
            {
                field: "created",
                headerName: t("fleetManagementView.tachogramDownloads.created"),
                flex: 1.5,
                valueFormatter: (params) =>
                    formatUnixTimestamp(params.value as number),
            },
            {
                field: "name",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.fileName"
                ),
                flex: 2,
            },
            {
                field: "registerNumber",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.vehicleRegistration"
                ),
                flex: 1.5,
            },
            {
                field: "vin",
                headerName: t("fleetManagementView.tachogramDownloads.vin"),
                flex: 1.5,
                valueGetter: (params) => params.row.meta?.vin ?? "",
            },
            {
                field: "size",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.fileSize"
                ),
                flex: 1,
                valueFormatter: (params) =>
                    formatFileSize(params.value as number),
            },
            {
                field: "download",
                headerName: t(
                    "fleetManagementView.tachogramDownloads.download"
                ),
                flex: 0.5,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <IconButton
                        size="small"
                        onClick={() => window.open(params.row.url, "_blank")}
                    >
                        <DownloadIcon />
                    </IconButton>
                ),
            },
        ],
        [t]
    );

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    alignItems: "flex-start",
                }}
            >
                <Box sx={{ "& > div": { minWidth: "200px" } }}>
                    <MuiDateTimePicker
                        dateLabel={t(
                            "fleetManagementView.tachogramDownloads.fromDate"
                        )}
                        value={fromDate}
                        onChange={(value) => setFromDate(value)}
                        dateOnly={true}
                        asUtc={false}
                    />
                </Box>
                <Box sx={{ "& > div": { minWidth: "200px" } }}>
                    <MuiDateTimePicker
                        dateLabel={t(
                            "fleetManagementView.tachogramDownloads.toDate"
                        )}
                        value={toDate}
                        onChange={(value) => setToDate(value)}
                        dateOnly={true}
                        asUtc={false}
                    />
                </Box>
                <TextField
                    label={t("fleetManagementView.tachogramDownloads.search")}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </Box>
            <LinkTabs
                tabs={[
                    {
                        value: "drivers",
                        label: t(
                            "fleetManagementView.tachogramDownloads.drivers"
                        ),
                        to: `${BASE_PATH}/drivers`,
                        children: (
                            <Box>
                                <MuiDataGrid
                                    title={t(
                                        "fleetManagementView.tachogramDownloads.tabTitle"
                                    )}
                                    density="compact"
                                    columns={driverFileColumns}
                                    rows={filteredDriverFiles}
                                    getRowId={(row: DriverFileRow) => row.uuid}
                                    loading={driverFilesLoading}
                                    pagination
                                    initialState={{
                                        pagination: {
                                            paginationModel: { pageSize: 10 },
                                        },
                                    }}
                                    pageSizeOptions={[10, 25, 50]}
                                    disableToolbar
                                />
                                {employeesWithoutCard.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ mb: 1 }}
                                        >
                                            {t(
                                                "fleetManagementView.tachogramDownloads.employeesWithoutCard"
                                            )}
                                        </Typography>
                                        <MuiDataGrid
                                            title={t(
                                                "fleetManagementView.tachogramDownloads.employeesWithoutCard"
                                            )}
                                            density="compact"
                                            columns={noCardColumns}
                                            rows={employeesWithoutCard}
                                            getRowId={(
                                                row: EmployeeWithoutCard
                                            ) => row.employeeId}
                                            loading={driverFilesLoading}
                                            pagination
                                            initialState={{
                                                pagination: {
                                                    paginationModel: {
                                                        pageSize: 10,
                                                    },
                                                },
                                            }}
                                            pageSizeOptions={[10, 25, 50]}
                                            disableToolbar
                                        />
                                    </Box>
                                )}
                            </Box>
                        ),
                    },
                    {
                        value: "vehicles",
                        label: t(
                            "fleetManagementView.tachogramDownloads.vehicles"
                        ),
                        to: `${BASE_PATH}/vehicles`,
                        children: (
                            <MuiDataGrid
                                title={t(
                                    "fleetManagementView.tachogramDownloads.tabTitle"
                                )}
                                density="compact"
                                columns={vehicleFileColumns}
                                rows={filteredVehicleFiles}
                                getRowId={(row: VehicleFileRow) => row.uuid}
                                loading={vehicleFilesLoading}
                                pagination
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10 },
                                    },
                                }}
                                pageSizeOptions={[10, 25, 50]}
                                disableToolbar
                            />
                        ),
                    },
                ]}
            />
        </Box>
    );
};
